import { useCallback } from 'react';

import type {
  ApprovalAction,
  ApprovalData,
  ApprovalType,
} from 'ui-product-approvals-common';

import type {
  AppHubUserRecord,
  AppTeamRecord,
  BreezeAgentRecord,
} from '../types/CrmSearchUser';
import { captureException } from '../utils/sentryUtils';
import { assertApprovalDataResolved } from '../utils/assertApprovalDataResolved';
import { toApprovalData } from '../utils/approvalResponseAdapters';
import { useMarkChangesResolvedMutation } from '../../__generated__/chirp/com/hubspot/product/approvals/rpc/ApprovalsRpc.hooks';
import { GET_LATEST_APPROVAL_QUERY } from '../../__generated__/chirp/com/hubspot/product/approvals/rpc/ApprovalsRpc.dfc.new';
import { APPROVAL_TYPE_TO_OBJECT_TYPE_ID } from 'ui-product-approvals-common/constants/approvalTypeIds';

interface UseReRequestReviewArgs {
  approvalType: ApprovalType;
  approvalAction: ApprovalAction;
  approvableObjectId: number | undefined;
  usersRecord: AppHubUserRecord;
  agentsRecord: BreezeAgentRecord;
  teamsRecord: AppTeamRecord;
  resolvedData: ApprovalData;
}

export const useReRequestReview = ({
  approvalType,
  approvalAction,
  approvableObjectId,
  usersRecord,
  agentsRecord,
  teamsRecord,
  resolvedData,
}: UseReRequestReviewArgs) => {
  const [markChangesResolvedMutation] = useMarkChangesResolvedMutation();

  const reRequestReview = useCallback(
    (approvalStepId: number, message?: string): Promise<ApprovalData> => {
      const validated = assertApprovalDataResolved(
        'useReRequestReview',
        resolvedData,
        approvableObjectId
      );
      return markChangesResolvedMutation({
        variables: {
          approvalStepId,
          message,
        },
        update: (cache, { data: mutationData }) => {
          const approvalData = mutationData?.result?.approval;
          if (approvalData) {
            cache.writeQuery({
              query: GET_LATEST_APPROVAL_QUERY,
              variables: {
                approvalTypeId: APPROVAL_TYPE_TO_OBJECT_TYPE_ID[approvalType],
                approvalAction,
                approvableObjectId: validated.approvableObjectId,
              },
              data: { result: { approval: approvalData } },
            });
          }
        },
      })
        .then((result) => {
          const approval = result?.data?.result?.approval;
          if (approval) {
            return toApprovalData(
              approval,
              usersRecord,
              agentsRecord,
              approvalType,
              approvalAction,
              teamsRecord
            );
          } else {
            throw new Error(
              'reRequestReview succeeded but returned no approval data'
            );
          }
        })
        .catch((error) => {
          captureException(error, {
            extra: {
              fingerprint: 'useApprovalData-reRequestReview',
              approvalType,
              approvalAction,
              approvalId: validated.resolvedData.id,
            },
          });
          throw error;
        });
    },
    [
      markChangesResolvedMutation,
      resolvedData,
      usersRecord,
      agentsRecord,
      teamsRecord,
      approvalType,
      approvalAction,
      approvableObjectId,
    ]
  );

  return reRequestReview;
};
